/**
 * This middleware persists promo codes and the attribution super cookie on app
 * load, reading them from the URL for downstream attribution readers.
 */
import { setCookie } from 'stash/utils/cookies';
import { STASH_INIT } from 'stash/actions/init';
import { getCookie, getURLParam, isMobileWeb } from 'stash/utils';
import {
	SESSION_STARTED_EVENT,
	buildSessionStartedProperties,
	createSuperCookie,
	setSuperCookie,
} from 'stash/utils/superCookie';
import type { Middleware } from 'redux';

export const PROMO_COOKIE_NAME = 'stashPromoCode';
export const PROMO_V2_COOKIE_NAME = 'stashPromoCodeV2';
export const PROMO_PARAM_NAME = 'stash_code';
export const PROMO_V2_PARAM_NAME = 'stash_code_v2';
const MAX_COOKIE_AGE = 1000 * 60 * 60 * 24 * 15; // 15 days

type PromoAction = { type: typeof STASH_INIT };

type SegmentAnalytics = {
	track: (
		event: string,
		properties: Record<string, string>,
		options?: { context?: Record<string, unknown> }
	) => unknown;
};

function createWebViewContext() {
	return {
		originatingApp: getCookie('nativePlatform') || 'web',
		originatingAppVersion: getCookie('nativeAppVersion'),
		originatingAppId: getCookie('nativeAppID'),
	};
}

function emitSessionStarted(
	analytics: SegmentAnalytics,
	cookie: NonNullable<ReturnType<typeof createSuperCookie>['cookie']>,
	now: Date
): void {
	const payloadId = crypto.randomUUID();
	analytics.track(
		SESSION_STARTED_EVENT,
		buildSessionStartedProperties(cookie, payloadId, now),
		{
			context: {
				stash_clean: true,
				isMobileWeb: isMobileWeb(),
				...createWebViewContext(),
			},
		}
	);
}

const promo =
	(analytics: SegmentAnalytics): Middleware =>
	() =>
	(next) =>
	(action) => {
		const typedAction = action as PromoAction;

		// Persist promocode in a cookie on load.
		if (typedAction.type === STASH_INIT) {
			if (getURLParam(PROMO_V2_PARAM_NAME)) {
				setCookie(PROMO_V2_COOKIE_NAME, getURLParam(PROMO_V2_PARAM_NAME), {
					domain: '.stash.com',
					maxAge: MAX_COOKIE_AGE,
				});
			} else if (getURLParam(PROMO_PARAM_NAME)) {
				setCookie(PROMO_COOKIE_NAME, getURLParam(PROMO_PARAM_NAME), {
					domain: '.stash.com',
					maxAge: MAX_COOKIE_AGE,
				});
			}

			const now = new Date();
			const { cookie: superCookie, shouldEmitSessionStarted } = createSuperCookie({ now });
			if (superCookie) {
				setSuperCookie(superCookie);
				if (shouldEmitSessionStarted) {
					try {
						emitSessionStarted(analytics, superCookie, now);
					} catch (error) {
						console.warn('Failed to emit Session_Started:', error);
					}
				}
			}
		}

		return next(action);
	};

export default promo;
